/* eslint-disable @next/next/no-img-element */
import React, { Fragment, useEffect, useMemo, useRef } from "react";
import dynamic from "next/dynamic";
import Script from "next/script";
import Head from "next/head";

import Header from "../../components/Header";
import TextLayout from "../../evs-components/TextLayout";
import SocialMedia from "../../components/SocialMedia";
import Newsletter from "../../components/Newsletter";
import GoogleAd from "../../components/GoogleAd";

import { googleAdIds, links } from "../../helper/constants";
import { event } from "../../lib/gtag";

import styles from "./styles.module.scss";

import DrivingExperienceBanner from "../../public/assets/images/ev-show/driving-experience-banner.png";
import DrivingExperienceInsideBanner from "../../public/assets/images/ev-show/inside-driving-experience-banner.png";

import BankOfIrelandLogo from "../../public/assets/images/ev-show/bank-of-ireland.png";
import Zevi from "../../public/assets/images/ev-show/zevi.png";
import SSE_Airtricity from "../../public/assets/images/ev-show/SSEAirtricity.png";
import IEVA from "../../public/assets/images/ev-show/ieva.png";
import IEVANI from "../../public/assets/images/ev-show/eva-ni.png";

import MGImage from "../../public/assets/images/ev-show/mg.png";
import AudiImage from "../../public/assets/images/ev-show/audi.png";
import CupraImage from "../../public/assets/images/ev-show/cupra.png";
import FordImage from "../../public/assets/images/ev-show/ford.png";
import DaciaImage from "../../public/assets/images/ev-show/dacia.png";
import SmartImage from "../../public/assets/images/ev-show/smart.png";
import SubaruImage from "../../public/assets/images/ev-show/subaru.png";
import LexusImage from "../../public/assets/images/ev-show/lexus.png";
import ORAImage from "../../public/assets/images/ev-show/ora.png";
import AlfaRomeoImage from "../../public/assets/images/ev-show/alfa-romeo.png";
import BYDImage from "../../public/assets/images/ev-show/byd.png";
import PorscheImage from "../../public/assets/images/ev-show/porsche.png";
import CitroenImage from "../../public/assets/images/ev-show/citroen.png";
import JeepImage from "../../public/assets/images/ev-show/jeep.png";
import NissanImage from "../../public/assets/images/ev-show/nissan.png";
import PeugeotImage from "../../public/assets/images/ev-show/peugeot.png";
import HyundaiImage from "../../public/assets/images/ev-show/hyundai.png";
import VolvoImage from "../../public/assets/images/ev-show/volvo.png";
import MaxusImage from "../../public/assets/images/ev-show/maxus.png";
import OpelImage from "../../public/assets/images/ev-show/opel.png";
import FiatImage from "../../public/assets/images/ev-show/fiat.png";
import ToyotaImage from "../../public/assets/images/ev-show/toyota.png";
import KiaImage from "../../public/assets/images/ev-show/kia.png";
import SkodaImage from "../../public/assets/images/ev-show/skoda.png";
import BMWImage from "../../public/assets/images/ev-show/bmw.png";
import MercedesBenzImage from "../../public/assets/images/ev-show/mercedes-benz.png";
import RenoImage from "../../public/assets/images/ev-show/reno.png";
import VolkswagenImage from "../../public/assets/images/ev-show/volkswagen.png";
import FusoImage from "../../public/assets/images/ev-show/fuso.png";
import TeslaImage from "../../public/assets/images/ev-show/tesla.png";
import PolestarImage from "../../public/assets/images/ev-show/polestar.png";
import YangwangImage from "../../public/assets/images/ev-show/yangwang.png";
import MiniImage from "../../public/assets/images/ev-show/mini.png";
import KgmImage from "../../public/assets/images/ev-show/kgm.png";

import AxaImage from "../../public/assets/images/ev-show/axa.png";
import EpowerImage from "../../public/assets/images/ev-show/epower.png";
import EasygoImage from "../../public/assets/images/ev-show/easygo.png";
import EncomImage from "../../public/assets/images/ev-show/encom.png";
import EnergiaImage from "../../public/assets/images/ev-show/energia.png";
import MyenergiImage from "../../public/assets/images/ev-show/myenergi.png";
import WeevImage from "../../public/assets/images/ev-show/weev.png";
import StartSolarImage from "../../public/assets/images/ev-show/start-solar.png";
import TheBuggymanImage from "../../public/assets/images/ev-show/the-buggyman.png";
import TradebidImage from "../../public/assets/images/ev-show/tradebid.png";
import GeotabImage from "../../public/assets/images/ev-show/geotab.png";
import Seai from "../../public/assets/images/ev-show/seai.png";
import Blink from "../../public/assets/images/ev-show/blink.png";
import BestDrive from "../../public/assets/images/ev-show/best-drive.png";
import Watt from "../../public/assets/images/ev-show/wattCharger.png";
import Esb from "../../public/assets/images/ev-show/esb.png";
import Evhacs from "../../public/assets/images/ev-show/evhacs.png";
import DoneDeal from "../../public/assets/images/ev-show/done-deal.png";

import EstationImage from "../../public/assets/images/ev-show/eStation.png";
import GardaImage from "../../public/assets/images/ev-show/garda.png";
import JfwImage from "../../public/assets/images/ev-show/jfw.png";
import TrustapImage from "../../public/assets/images/ev-show/trustap.png";
import InstavoltImage from "../../public/assets/images/ev-show/instavolt.png";
import SolarSmartImage from "../../public/assets/images/ev-show/solar-smart.png";
import { useIsBoiWebsite } from "../../components/Context/WebsiteType";
import { copyrightDisplayText } from "../../helper/functions/copyrightDisplayText";
import Footer from "../../components/Footer";

const YoutubeEmbed = dynamic(() => import("../../components/YouTubeEmbed"), { ssr: false });

interface LogoProps {
	src: string;
	alt: string;
	url?: string;
}

interface DrivingExperienceCardProps {
	location: string;
	date: string;
	time: string;
	venue: string;
	logos: React.ReactNode;
	registerLink: string;
	onRegisterClick: () => void;
	styles: { [key: string]: string };
	scrollActive?: boolean;
}

const brands: LogoProps[] = [
	{
		src: MGImage.src,
		alt: "MG",
		url: `${links.nevo_website}${links.private}/mg`
	},
	{
		src: AudiImage.src,
		alt: "Audi",
		url: `${links.nevo_website}${links.private}/audi`
	},
	{
		src: CupraImage.src,
		alt: "Cupra",
		url: `${links.nevo_website}${links.private}/cupra`
	},
	{
		src: BYDImage.src,
		alt: "BYD",
		url: `${links.nevo_website}${links.private}/byd`
	},
	{
		src: PorscheImage.src,
		alt: "PORSCHE",
		url: `${links.nevo_website}${links.private}/porsche`
	},
	{
		src: CitroenImage.src,
		alt: "CITROEN",
		url: `${links.nevo_website}${links.private}/citroen`
	},
	{
		src: MercedesBenzImage.src,
		alt: "Mercedes-Benz",
		url: `${links.nevo_website}${links.private}/mercedes-benz`
	},
	{
		src: JeepImage.src,
		alt: "Jeep",
		url: `${links.nevo_website}${links.private}/jeep`
	},
	{
		src: NissanImage.src,
		alt: "Nissan",
		url: `${links.nevo_website}${links.private}/nissan`
	},
	{
		src: PeugeotImage.src,
		alt: "Peugeot",
		url: `${links.nevo_website}${links.private}/peugeot`
	},
	{
		src: HyundaiImage.src,
		alt: "Hyundai",
		url: `${links.nevo_website}${links.private}/hyundai`
	},
	{
		src: VolvoImage.src,
		alt: "Volvo",
		url: `${links.nevo_website}${links.private}/volvo`
	},
	{
		src: MaxusImage.src,
		alt: "Maxus",
		url: `${links.nevo_website}${links.private}/maxus`
	},
	{
		src: OpelImage.src,
		alt: "Opel",
		url: `${links.nevo_website}${links.private}/opel`
	},
	{
		src: FiatImage.src,
		alt: "Fiat",
		url: `${links.nevo_website}${links.private}/fiat`
	},
	{
		src: FordImage.src,
		alt: "Ford",
		url: `${links.nevo_website}${links.private}/ford`
	},
	{
		src: DaciaImage.src,
		alt: "Dacia"
	},
	{
		src: ToyotaImage.src,
		alt: "Toyota",
		url: `${links.nevo_website}${links.private}/toyota`
	},
	{
		src: KiaImage.src,
		alt: "Kia",
		url: `${links.nevo_website}${links.private}/kia`
	},
	{
		src: SkodaImage.src,
		alt: "Skoda",
		url: `${links.nevo_website}${links.private}/skoda`
	},
	{
		src: BMWImage.src,
		alt: "BMW",
		url: `${links.nevo_website}${links.private}/bmw`
	},
	{
		src: RenoImage.src,
		alt: "Reno",
		url: `${links.nevo_website}${links.private}/reno`
	},
	{
		src: SmartImage.src,
		alt: "Smart",
		url: `${links.nevo_website}${links.private}/smart`
	},
	{
		src: VolkswagenImage.src,
		alt: "Volkswagen",
		url: `${links.nevo_website}${links.private}/volkswagen`
	},
	{
		src: LexusImage.src,
		alt: "Lexus",
		url: `${links.nevo_website}${links.private}/lexus`
	},
	{
		src: AlfaRomeoImage.src,
		alt: "Alfa Romeo",
		url: `${links.nevo_website}${links.private}/alfa-romeo`
	},
	{
		src: ORAImage.src,
		alt: "ORA",
		url: `${links.nevo_website}${links.private}/ora`
	},
	{
		src: SubaruImage.src,
		alt: "Subaru",
		url: `${links.nevo_website}${links.private}/subaru`
	},
	{
		src: FusoImage.src,
		alt: "Fuso",
		url: `${links.nevo_website}${links.private}/fuso`
	},
	{
		src: TeslaImage.src,
		alt: "Tesla",
		url: `${links.nevo_website}${links.private}/tesla`
	},
	{
		src: PolestarImage.src,
		alt: "Polestar",
		url: `${links.nevo_website}${links.private}/polestar`
	},
	{
		src: YangwangImage.src,
		alt: "Yangwang",
		url: `${links.nevo_website}${links.private}/yangwang`
	},
	{
		src: MiniImage.src,
		alt: "Mini",
		url: `${links.nevo_website}${links.private}/mini`
	},
	{
		src: KgmImage.src,
		alt: "KGM",
		url: `${links.nevo_website}${links.private}/kgm`
	}
];

const vendors: LogoProps[] = [
	{
		src: AxaImage.src,
		alt: "Axa"
	},
	{
		src: EpowerImage.src,
		alt: "E-Power"
	},
	{
		src: EasygoImage.src,
		alt: "Easy Go"
	},
	{
		src: WeevImage.src,
		alt: "weev"
	},
	{
		src: MyenergiImage.src,
		alt: "myenergi"
	},
	{
		src: EncomImage.src,
		alt: "Encom"
	},
	{
		src: StartSolarImage.src,
		alt: "Start Solar"
	},
	{
		src: TheBuggymanImage.src,
		alt: "The Buggyman"
	},
	{
		src: EnergiaImage.src,
		alt: "Energia"
	},
	{
		src: GeotabImage.src,
		alt: "Geotab"
	},
	{
		src: EstationImage.src,
		alt: "E-station"
	},
	{
		src: TradebidImage.src,
		alt: "Tradebid",
		url: links.tradebid_website
	},
	{
		src: Seai.src,
		alt: "Seai"
	},
	{
		src: Blink.src,
		alt: "Blink"
	},
	{
		src: BestDrive.src,
		alt: "BestDrive"
	},
	{
		src: Watt.src,
		alt: "Watt"
	},
	{
		src: Esb.src,
		alt: "ESB"
	},
	{
		src: GardaImage.src,
		alt: "Garda"
	},
	{
		src: Evhacs.src,
		alt: "EVHACS"
	},
	{
		src: DoneDeal.src,
		alt: "DoneDeal"
	},
	{
		src: JfwImage.src,
		alt: "JFW"
	},
	{
		src: TrustapImage.src,
		alt: "TrustApp"
	},
	{
		src: InstavoltImage.src,
		alt: "Instavolt"
	},
	{
		src: SolarSmartImage.src,
		alt: "Solar Smart"
	}
];

const kildareDrivingExperienceLogos = [
	{
		src: OpelImage.src,
		alt: "Opel",
		url: `${links.nevo_website}${links.private}/opel`
	},
	{
		src: MercedesBenzImage.src,
		alt: "MercedesBenz",
		url: `${links.nevo_website}${links.private}/mercedes-benz`
	},
	{
		src: SmartImage.src,
		alt: "Smart",
		url: `${links.nevo_website}${links.private}/smart`
	},
	{
		src: HyundaiImage.src,
		alt: "Hyundai",
		url: `${links.nevo_website}${links.private}/hyundai`
	},
	{
		src: RenoImage.src,
		alt: "Renault",
		url: `${links.nevo_website}${links.private}/renault`
	},
	{
		src: NissanImage.src,
		alt: "Nissan",
		url: `${links.nevo_website}${links.private}/nissan`
	},
	{
		src: FordImage.src,
		alt: "Ford",
		url: `${links.nevo_website}${links.private}/ford`
	},
	{
		src: JeepImage.src,
		alt: "Jeep",
		url: `${links.nevo_website}${links.private}/jeep`
	},
	{
		src: FiatImage.src,
		alt: "Fiat",
		url: `${links.nevo_website}${links.private}/fiat`
	},
	{
		src: BMWImage.src,
		alt: "BMW",
		url: `${links.nevo_website}${links.private}/bmw`
	},
	{
		src: ToyotaImage.src,
		alt: "Toyota",
		url: `${links.nevo_website}${links.private}/toyota`
	}
];

export default function ElectricVehicleShowPage() {
	// const tabletBreakPoint = useMobile(1030);
	const contentRef = useRef<HTMLParagraphElement>(null);
	const viewDatesRef = useRef<HTMLParagraphElement>(null);
	const hasSupportedLogos = [Zevi, IEVA, IEVANI].every((logo) => !!logo.src);

	const isBoiWebsite = useIsBoiWebsite();

	// Updating body color to black
	useEffect(() => {
		const bodyElement = document.querySelector("body");

		if (bodyElement) {
			bodyElement.style.background = "#1A1C20";
		}

		return () => {
			if (bodyElement) {
				bodyElement.style.background = "";
			}
		};
	}, []);

	const renderLogos = (logos: LogoProps[]) => {
		return logos.map((logo, index) => {
			if (!!logo.url) {
				let text = logo.alt.replace(" ", "").toLowerCase();
				text = text.charAt(0).toUpperCase() + text.slice(1);
				const eventTag = `evShowPage${text}Click`;

				return (
					<a className={styles.logoHolder} href={logo.url} key={index} target="_blank" rel="noreferrer" onClick={() => event(eventTag)}>
						<img className={styles.logo} src={logo.src} alt={logo.alt} />
					</a>
				);
			} else {
				return (
					<div className={styles.logoHolder} key={index}>
						<img className={styles.logo} src={logo.src} alt={logo.alt} />
					</div>
				);
			}
		});
	};

	const renderedPartnerAndCollaboration = useMemo(() => {
		return (
			<div className={styles.sponsorContainer}>
				<div className={styles.describedLogos}>
					<div className={styles.describedLogoItem}>
						<p className={styles.text}>In partnership with</p>

						<div className={styles.logosHolder}>
							<a href={`${links.nevo_website}${links.finance_partner}`} target="_blank" rel="noreferrer" onClick={() => event("evShowPageBankOfIrelandClick")}>
								<div className={`${styles.logo} ${styles.boiLogo}`} style={{ backgroundImage: `url(${BankOfIrelandLogo.src})` }}></div>
							</a>
						</div>
					</div>

					<div className={styles.divider}></div>

					<div className={styles.describedLogoItem}>
						<p className={styles.text}>Powered by</p>

						<div className={styles.logosHolder}>
							<div className={`${styles.logo} ${styles.sseLogo}`} style={{ backgroundImage: `url(${SSE_Airtricity.src})` }}></div>
						</div>
					</div>
				</div>
			</div>
		);
	}, []);

	const DrivingExperienceCard: React.FC<DrivingExperienceCardProps> = ({ location, date, time, venue, logos, registerLink, onRegisterClick, styles, scrollActive }) => {
		return (
			<div ref={scrollActive ? viewDatesRef : null} id="viewDates" className={`${styles.datesContentCard} ${styles.chargedUpCard} ${styles.brands}`}>
				<h1 className={`${styles.header} ${styles.blackText}`}>
					<span className={styles.blueText}>Nevo Driving Experience: </span>
					{location}
				</h1>

				<div className={styles.whenWhatContainer}>
					<TextLayout
						header="When is it?"
						isGreyHeader={true}
						text={
							<Fragment>
								{date}. {time}
							</Fragment>
						}
					/>
					<TextLayout header="Where is it?" isGreyHeader={true} text={venue} />
				</div>

				<div className={styles.logosHolder}>{logos}</div>

				<a href={registerLink} target="_blank" rel="noreferrer" onClick={onRegisterClick}>
					<button className={`${styles.blueButton} blue-button`}>Register Now</button>
				</a>
			</div>
		);
	};

	const onScrollToViewDates = () => {
		viewDatesRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	return (
		<Fragment>
			<Head>
				<title>Electric Vehicle Show | Nevo</title>
			</Head>

			<Header transparent hideBanner />

			<div className={`${styles.evShowPage} ${isBoiWebsite ? styles.boi : styles.nevo}`}>
				{!isBoiWebsite && <GoogleAd adId={googleAdIds.horizontal_banner} displayId="top" evShowPage />}
				<div className={styles.container}>
					<div className={styles.evShowBanner} style={{ backgroundImage: `url(${DrivingExperienceBanner.src})` }}>
						<div className={styles.bannerContent}>
							<img className={styles.bannerImage} src={DrivingExperienceInsideBanner.src} alt="Nevo Driving Experience Show" />

							<div className={styles.holder}>
								<p className={styles.smallBlueText}>Register Now to Participate</p>
								<button className={`${styles.blueButton} ${styles.whiteButton} blue-button`} onClick={onScrollToViewDates}>
									View Dates
								</button>
								<p className={styles.poweredBy}>Powered by</p>
								<div className={styles.logo} style={{ backgroundImage: `url(${SSE_Airtricity.src})` }}></div>
							</div>
						</div>
					</div>

					<h1 className={`${styles.nevoHeader} ${styles.header} ${styles.whiteText}`}>
						Discover Nevo <span className={styles.blueText}>Driving Experience</span> Events.
					</h1>

					<p className={`${styles.styledP} ${styles.nevoDescription}`} ref={contentRef}>
						Welcome to the Nevo Driving Experience – your chance to get behind the wheel of the latest electric vehicles and learn everything you need to switch to electric motoring.
						Taking place across Ireland, these free-to-attend events bring the Nevo EV Show to your doorstep, connecting you with local experts and top franchise dealerships for an
						unrivalled test drive experience. Discover the newest electric models and gain valuable insights into charging, battery care, ownership costs, and more. Register now for your
						free ticket below.
					</p>

					<Script src="https://www.youtube.com/iframe_api" strategy="afterInteractive" />

					<YoutubeEmbed videoURL="https://www.youtube.com/watch?v=-OuffkN5A_8" playVideo={true} onPage onPageClassName={styles.videoSection} />

					<div className={styles.sponsors}>{renderedPartnerAndCollaboration}</div>

					<div className={`${styles.contentCard} ${styles.expectations}`}>
						<h1 className={`${styles.header} ${styles.whiteText}`}>What to Expect</h1>

						<div className={styles.textHolder}>
							<h2 className={styles.blueHeader}>Discover the Ultimate Test Drive Events</h2>
							<p className={styles.styledP}>
								Get behind the wheel and experience the thrill of electric driving with the latest EVs from Ireland&apos;s most popular brands. Offering you the unique opportunity to
								test drive and compare electric vehicles from multiple brands, all in one convenient location.
							</p>
						</div>

						<div className={styles.textHolder}>
							<h2 className={styles.blueHeader}>Explore the Latest Electric Vehicles</h2>
							<p className={styles.styledP}>
								Discover and compare the newest electric and hybrid vehicles from Ireland&apos;s top automotive manufacturers. From sleek performance cars to versatile family SUVs and
								compact city cars, get a glimpse of the latest EVs to hit the market.
							</p>
						</div>

						<div className={styles.textHolder}>
							<h2 className={styles.blueHeader}>Meet with Industry Experts</h2>
							<p className={styles.styledP}>
								Connect with industry leaders and representatives from Ireland&apos;s top automotive brands, as well as experts from local dealerships and leading charging providers.
								They&apos;ll be on hand to answer your questions and guide you through a smooth and seamless transition to electric driving.
							</p>
						</div>
					</div>

					<div className={`${styles.headerWrapper}`}>
						<h1 className={`${styles.nevoHeader} ${styles.header} ${styles.whiteText}`}>Find Electric Driving Experiences Near You.</h1>
						<h2 className={styles.blueHeader}>Don’t see your county? Stay tuned for new EV events coming soon!</h2>
					</div>

					<DrivingExperienceCard
						location="Kildare"
						date="Sunday 15th June 2025"
						time="10:30am - 4:30pm"
						venue="Curragh Racecourse, Kildare."
						logos={renderLogos(kildareDrivingExperienceLogos)}
						registerLink={links.nevo_driving_experience_kildare_event}
						onRegisterClick={() => event("evShowPageRegisterTopBannerClick")}
						styles={styles}
						scrollActive={true}
					/>

					<div className={`${styles.contentCard} ${styles.form}`}>
						<h1 className={`${styles.header} ${styles.whiteText}`}>
							We&apos;re <span className={styles.blueText}>here to help</span> you go electric.
						</h1>

						<p className={`${styles.bigText} ${styles.joinNevoText}`}>Stay up to date with event updates and all things electric motoring.</p>

						<Newsletter whiteVersion big noHeader className={styles.newsletterSubscription} />

						<h1 className={`${styles.header} ${styles.visitUs} ${styles.whiteText}`}>Ireland&apos;s only dedicated EV platform.</h1>

						<div className={styles.buttonHolder}>
							<a href={`${links.private}/all`} target="_blank" rel="noreferrer" onClick={() => event("evShowPageViewAllVehiclesClick")}>
								<button className={`${styles.blueButton} ${styles.whiteButton} blue-button`}>View All Vehicles</button>
							</a>

							<a href={`${links.videos}`} target="_blank" rel="noreferrer" onClick={() => event("evShowPageViewEVReviewsClick")}>
								<button className={`${styles.blueButton} ${styles.whiteButton} blue-button`}>EV Reviews</button>
							</a>
						</div>
					</div>

					{!isBoiWebsite && (
						<div className={styles.followLink}>
							{!isBoiWebsite && <GoogleAd adId={googleAdIds.horizontal_banner} displayId="bottom" evShowPage />}
							<a className={`${styles.nevoLink} ${styles.bigText}`} href={links.nevo_website} target="_blank" rel="noreferrer" onClick={() => event("evShowPageNevoWebsiteClick")}>
								www.nevo.ie
							</a>
							<SocialMedia whiteVersion noFollow className={styles.socialMedia} />

							<p className={`${styles.copyright} copyright-mark`}>{copyrightDisplayText(false)}</p>
						</div>
					)}
				</div>
			</div>
			{isBoiWebsite && <Footer />}
		</Fragment>
	);
}
